import type {
  Theme,
  CSSObject,
  Components,
  ComponentsVariants,
} from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { buttonClasses } from '@mui/material/Button';

import { motion } from '../../motion';
import { colorKeys } from '../palette';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../../extend-theme-types.d.ts}
 */
export type ButtonExtendSize = { mini: true; xLarge: true };
export type ButtonExtendVariant = { soft: true };
export type ButtonExtendColor = { black: true; white: true };

type ButtonVariants = ComponentsVariants<Theme>['MuiButton'];

const baseColors = ['inherit'] as const;
const allColors = [
  ...baseColors,
  ...colorKeys.palette,
  ...colorKeys.common,
] as const;

const DIMENSIONS: Record<
  'mini' | 'small' | 'medium' | 'large' | 'xLarge',
  CSSObject
> = {
  mini: {
    '--padding-y': '2px',
    '--padding-x': '6px',
    minHeight: 24,
    lineHeight: 18 / 12,
    fontSize: 12,
  },
  small: {
    '--padding-y': '4px',
    '--padding-x': '8px',
    minHeight: 30,
    lineHeight: 22 / 13,
  },
  medium: {
    '--padding-y': '9.5px',
    '--padding-x': '16px',
    minHeight: 40,
    lineHeight: 24 / 14,
  },
  large: {
    '--padding-y': '8px',
    '--padding-x': '16px',
    minHeight: 48,
    lineHeight: 26 / 15,
  },
  xLarge: { minHeight: 56 },
};

/* **********************************************************************
 * 🗳️ Variants
 * **********************************************************************/
const containedVariants = [
  {
    props: (props) =>
      props.variant === 'contained' && props.color === 'inherit',
    style: ({ theme }) => ({
      ...theme.mixins.filledStyles(theme, 'inherit'),
    }),
  },
  ...(colorKeys.common.map((colorKey) => ({
    props: (props) => props.variant === 'contained' && props.color === colorKey,
    style: ({ theme }) => ({
      ...theme.mixins.filledStyles(theme, colorKey),
    }),
  })) satisfies ButtonVariants),
  // Primary gradient button - placed last for highest specificity
  {
    props: (props) =>
      props.variant === 'contained' && props.color === 'primary',
    style: ({ theme }) => ({
      background: theme.gradients.primary.background,
      color: theme.vars.palette.common.white,
      position: 'relative',
      isolation: 'isolate',
      [`& .${buttonClasses.startIcon}, & .${buttonClasses.endIcon}`]: {
        color: 'inherit',
      },
      // Gradients (background-image) can't be CSS-transitioned, so the hover
      // gradient is layered on ::before (z-index:-1, below the label) and
      // cross-faded via opacity instead of hard-swapping the background.
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        borderRadius: 'inherit',
        background: theme.gradients.primary.hover,
        opacity: 0,
        transition: `opacity ${motion.duration.fast} ${motion.ease.out}`,
      },
      '&:hover::before': {
        opacity: 1,
      },
    }),
  },
] satisfies ButtonVariants;

const outlinedVariants = [
  {
    props: (props) => props.variant === 'outlined',
    style: ({ theme }) => ({
      borderColor: varAlpha('currentColor', theme.vars.opacity.outlined.border),
      '&:hover': {
        borderColor: 'currentColor',
        boxShadow: '0 0 0 0.75px currentColor',
        backgroundColor: varAlpha(
          'currentColor',
          theme.vars.palette.action.hoverOpacity
        ),
      },
    }),
  },
  {
    props: (props) => props.variant === 'outlined' && props.color === 'inherit',
    style: ({ theme }) => ({
      borderColor: theme.vars.palette.shared.buttonOutlined,
      '&:hover': {
        backgroundColor: theme.vars.palette.action.hover,
      },
    }),
  },
  ...(colorKeys.common.map((colorKey) => ({
    props: (props) => props.variant === 'outlined' && props.color === colorKey,
    style: ({ theme }) => ({
      color: theme.vars.palette.common[colorKey],
    }),
  })) satisfies ButtonVariants),
] satisfies ButtonVariants;

const textVariants = [
  {
    props: (props) => props.variant === 'text',
    style: ({ theme }) => ({
      '&:hover': {
        backgroundColor: varAlpha(
          'currentColor',
          theme.vars.palette.action.hoverOpacity
        ),
      },
    }),
  },
  {
    props: (props) => props.variant === 'text' && props.color === 'inherit',
    style: ({ theme }) => ({
      '&:hover': {
        backgroundColor: theme.vars.palette.action.hover,
      },
    }),
  },
  ...(colorKeys.common.map((colorKey) => ({
    props: (props) => props.variant === 'text' && props.color === colorKey,
    style: ({ theme }) => ({
      color: theme.vars.palette.common[colorKey],
    }),
  })) satisfies ButtonVariants),
] satisfies ButtonVariants;

/** error.main reads at 3.07 to 1 on the dark background, so error words use a lighter or darker token. CLOUD-1421. */
const errorWordsVariants = [
  {
    props: (props) => (props.variant === 'outlined' || props.variant === 'text') && props.color === 'error',
    style: ({ theme }) => ({
      color: theme.vars.palette.error.dark,
      ...theme.applyStyles('dark', { color: theme.vars.palette.error.light }),
    }),
  },
] satisfies ButtonVariants;

const softVariants = [
  ...(allColors.map((colorKey) => ({
    props: (props) => props.variant === 'soft' && props.color === colorKey,
    style: ({ theme }) => ({
      ...theme.mixins.softStyles(theme, colorKey, { hover: true }),
      backgroundColor: theme.vars.palette.grey[900],
    }),
  })) satisfies ButtonVariants),
] satisfies ButtonVariants;

const sizeVariants = [
  {
    props: {},
    style: { padding: 'var(--padding-y) var(--padding-x)' },
  },
  {
    props: (props) => props.size === 'mini',
    style: { ...DIMENSIONS.mini },
  },
  {
    props: (props) => props.size === 'small',
    style: { ...DIMENSIONS.small },
  },
  {
    props: (props) => props.size === 'medium',
    style: { ...DIMENSIONS.medium },
  },
  {
    props: (props) => props.size === 'large' || props.size === 'xLarge',
    style: { ...DIMENSIONS.large },
  },
  {
    props: (props) => props.size === 'xLarge',
    style: ({ theme }) => ({
      ...DIMENSIONS.xLarge,
      fontSize: theme.typography.pxToRem(15),
    }),
  },
  {
    props: (props) => props.variant === 'outlined',
    style: {
      paddingTop: 'calc(var(--padding-y) - 4px)',
      paddingBottom: 'calc(var(--padding-y) - 4px)',
    },
  },
  {
    props: (props) => props.variant === 'text',
    style: {
      paddingLeft: 'calc(var(--padding-x) - 4px)',
      paddingRight: 'calc(var(--padding-x) - 4px)',
    },
  },
] satisfies ButtonVariants;

const disabledVariants = [
  {
    props: (props) => props.variant === 'soft',
    style: ({ theme }) => ({
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.vars.palette.action.disabledBackground,
      },
    }),
  },
] satisfies ButtonVariants;

/* **********************************************************************
 * 🧩 Components
 * **********************************************************************/
const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
  // ▼▼▼▼▼▼▼▼ 🎨 STYLE ▼▼▼▼▼▼▼▼
  styleOverrides: {
    root: ({ theme }) => ({
      fontFamily: theme.typography.fontFamily,
    }),
  },
};

const MuiButton: Components<Theme>['MuiButton'] = {
  // ▼▼▼▼▼▼▼▼ ⚙️ PROPS ▼▼▼▼▼▼▼▼
  defaultProps: {
    color: 'inherit',
    disableElevation: true,
  },
  // ▼▼▼▼▼▼▼▼ 🎨 STYLE ▼▼▼▼▼▼▼▼
  styleOverrides: {
    root: {
      transition: `transform ${motion.duration.instant} ${motion.ease.out}, background-color ${motion.duration.fast} ${motion.ease.out}, border-color ${motion.duration.fast} ${motion.ease.out}, color ${motion.duration.fast} ${motion.ease.out}`,
      '&:active': { transform: 'scale(0.96)' },
      variants: [
        ...containedVariants,
        ...outlinedVariants,
        ...textVariants,
        ...errorWordsVariants,
        ...softVariants,
        ...sizeVariants,
        ...disabledVariants,
      ],
    },
  },
};

/* **********************************************************************
 * 🚀 Export
 * **********************************************************************/
export const button: Components<Theme> = {
  MuiButton,
  MuiButtonBase,
};
